<template>
  <v-container fluid>
    <div class="variant-display">
      <div class="section-title mb-4">Variant(s)</div>

      <!-- Header Row for Column Labels -->
      <div class="variant-header">
        <div class="variant-name-header">Variant Name</div>
        <div class="upload-header">Image</div>
        <div class="default-header">Default</div>
        <div class="extra-params-header">Extra Parameters</div>
        <div class="delete-header"></div>
      </div>

      <div v-for="variant in variants" :key="variant.id" class="variant-block">
        <div class="variant-item">
          <div class="variant-left">
            <div class="variant-line">
              <!-- Variant Name -->
              <input
                type="text"
                v-model="variant.name"
                class="variant-name-input"
                placeholder="Variant Name"
              />

              <!-- Image Upload + Copy -->
              <div class="upload-wrapper">
                <v-btn
                  text
                  small
                  color="primary"
                  class="copy-image-btn"
                  @click="copyToClipboard(variant.permanentUrl)"
                  v-if="variant.preSignedUrl && variant.imageName"
                >
                  <v-icon small left>mdi-content-copy</v-icon>
                  Copy Image Link
                </v-btn>

                <div class="upload-area-fixed" @click="openFileInput(variant)">
                  <img
                    v-if="variant.preSignedUrl && variant.imageName"
                    :src="variant.preSignedUrl"
                    alt="Preview"
                    class="variant-image-fixed"
                  />
                  <div v-else class="upload-placeholder-content">
                    <div class="upload-instructions">
                      Drag & drop files or <span class="browse-link">Browse</span>
                    </div>
                    <div class="supported-format">
                      Supported: JPEG, PNG, GIF, SFG, HEIC, WEBP
                    </div>
                  </div>
                </div>

                <!-- File Input -->
                <input
                  type="file"
                  :ref="'fileInput_' + variant.id"
                  style="display: none"
                  accept="image/jpeg,image/png,image/gif,image/svg+xml,image/heic,image/webp"
                  @change="handleFileChange($event, variant)"
                />
              </div>

              <!-- Default Switch -->
              <div class="default-switch-container">
                <v-tooltip bottom :disabled="!variant.isDefault">
                  <template v-slot:activator="{ on, attrs }">
                    <v-switch
                      v-model="variant.isDefault"
                      inset
                      class="default-switch-compact"
                      :disabled="variant.isDefault"
                      @change="setAsDefault(variant)"
                      v-bind="attrs"
                      v-on="on"
                    ></v-switch>
                  </template>
                  <span>This variant is already set as default.</span>
                </v-tooltip>
              </div>

              <!-- Extra Params -->
              <div class="extra-params-wrapper">
                <input
                  type="text"
                  v-model="variant.extraParamsInput"
                  class="variant-extra-params-input"
                  :class="{ 'invalid-input': variant.paramError }"
                  placeholder="EX &img=123&kw=456"
                  @input="validateExtraParams(variant)"
                />
                <div v-if="variant.paramError" class="error-message">
                  {{ variant.paramError }}
                </div>
              </div>

              <!-- Delete Button -->
              <v-tooltip bottom v-if="!variant.isDefault">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="delete-variant-btn"
                    v-bind="attrs"
                    v-on="on"
                    @click="deleteVariantConfirm(variant)"
                  >
                    <v-icon small color="primary">mdi-delete</v-icon>
                  </div>
                </template>
                <span>Delete Variant</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>

      <div>
        <v-btn color="primary" class="add-variant-btn" @click="createVariant">ADD VARIANT</v-btn>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import offerVariants from "@/api/offerVariants";
import ProgressBar from "@/components/pages/affiliates/customAssets/ProgressBar.vue";
import axios from "axios";

export default {
  name: 'VariantManager',
  components: { ProgressBar },
  props: {
    variants: Array,
    offerId: Number
  },
  data() {
    return {
      dialogDelete: false,
      originalVariant: null,
      selectedVariantId: null,
      selectedVariant: {},
      selectedFile: null,
      uploaded: false,
      dialog: false,
      assetData: {
        imageUrl: null,
        imageFileName: null
      },
      isUploading: false,
      uploadProgress: 0,
      uploadError: false,
      uploadErrorMessage: '',
      infoDialogs: {
        variant: false,
        addVariant: false,
        extraParams: false
      },
    };
  },
  mounted() {
    this.variants.forEach(variant => {
      if (typeof variant.params === 'string') {
        try {
          variant.params = JSON.parse(variant.params);
        } catch (e) {
          console.error('Invalid params JSON', e);
          variant.params = [];
        }
      }

      if (!Array.isArray(variant.params)) {
        variant.params = [];
      }

      this.$set(variant, 'extraParamsInput', this.generateParamString(variant.params));
      this.$set(variant, 'paramError', '');

      this.validateExtraParams(variant); // Validate on mount
    });

    // Ensure at least one variant is default on mount
    this.ensureDefaultVariant();
  },
  methods: {
    validateExtraParams(variant) {
      const input = variant.extraParamsInput.trim();
      if (!input) {
        this.$set(variant, 'paramError', '');
        return true;
      }

      const regex = /^(&[a-zA-Z0-9]+=[a-zA-Z0-9-]+)*$/;
      const pairs = input.match(/&[a-zA-Z0-9]+=[a-zA-Z0-9-]+/g);

      if (!regex.test(input) || (pairs && pairs.join('') !== input)) {
        this.$set(variant, 'paramError', 'Invalid format. Use: &key=value');
        return false;
      }

      this.$set(variant, 'paramError', '');
      return true;
    },
    async updateSingleVariant(variant) {
      if (!this.validateExtraParams(variant)) {
        await this.$dialog.notify.error('Please fix extra params format before saving.', {
          position: 'top-right',
          timeout: 2000,
        });
        return;
      }

      await offerVariants.updateOfferVariant(
        variant.id,
        variant.name,
        '',
        variant.imageName || '',
        JSON.stringify(variant.params),
        variant.isDefault,
        this.offerId
      );
    },
    async deleteVariantConfirm(variant) {
      const confirmed = await this.$dialog.warning({
        title: 'Warning',
        text: `You are about to delete variant "<b>${variant.name}</b>". <p>Are you sure?</p>`
      });

      if (confirmed) {
        const wasDefault = variant.isDefault;
        const isDeleted = await offerVariants.deleteOfferVariant(variant.id, this.offerId);
        if (isDeleted) {
          this.variants = this.variants.filter(v => v.id !== variant.id);
          if (wasDefault) {
            this.ensureDefaultVariant();
          }
          await this.$dialog.notify.success('Variant deleted!');
        } else {
          await this.$dialog.notify.error('Failed to delete variant.');
        }
      }
    },
    ensureDefaultVariant() {
      if (this.variants.length === 0) return;

      const currentDefault = this.variants.find(v => v.isDefault);

      if (!currentDefault) {
        console.log(`Setting first variant as default`, this.variants);

        this.variants.forEach(v => {
          this.$set(v, 'isDefault', false);
        });

        const firstVariant = this.variants[0];
        this.$set(firstVariant, 'isDefault', true);
        this.updateSingleVariant(firstVariant);
      }
    },
    async setAsDefault(selectedVariant) {
      // Prevent turning off the only default variant
      if (!selectedVariant.isDefault) {
        const defaultVariants = this.variants.filter(v => v.isDefault);
        if (defaultVariants.length === 1 && defaultVariants[0].id === selectedVariant.id) {
          this.$set(selectedVariant, 'isDefault', true); // Revert the change
          this.$dialog.notify.warning('At least one variant must be default.', {
            position: 'top-right',
            timeout: 2000,
          });
          return;
        }
      }

      // Update all variants
      for (const variant of this.variants) {
        const wasDefault = variant.isDefault;
        variant.isDefault = variant.id === selectedVariant.id ? selectedVariant.isDefault : false;
        if (variant.isDefault !== wasDefault) {
          await this.updateSingleVariant(variant);
        }
      }

      // Ensure at least one variant is default
      await this.ensureDefaultVariant();
    },
    generateParamString(params) {
      if (!params || params.length === 0) return '';
      return params.map(param => `&${param.name}=${param.value}`).join('');
    },
    copyToClipboard(value) {
      console.log('Copied: ', value);
      if (!value || value === 'None') return;
      navigator.clipboard.writeText(value).then(() => {
        this.$dialog.notify.success('Copied to clipboard!');
      });
    },
    addVariant() {
      this.$emit('add-variant');
    },
    onVariantSelect() {
      this.selectedVariant = this.variants.find(v => v.id === this.selectedVariantId);

      if (this.selectedVariant.preSignedUrl) {
        this.assetData.imageUrl = this.selectedVariant.preSigned      } else {
        this.assetData.imageUrl = null;
      }
      this.originalVariant = { ...this.selectedVariant };
    },
    async uploadFile(variant) {
      if (!variant.selectedFile) return;

      const extension = variant.selectedFile.name.split('.').pop();
      try {
        const presignedURL = await offerVariants.offerVariantImageUploadUrl(
          variant.id,
          variant.name,
          extension,
          'PUT'
        );

        await axios.put(presignedURL, variant.selectedFile, {
          headers: { 'Content-Type': variant.selectedFile.type }
        });

        const imageUrl = await offerVariants.offerVariantImageUploadUrl(
          variant.id,
          variant.name,
          extension,
          'GET'
        );

        variant.preSignedUrl = imageUrl;
        variant.imageName = variant.selectedFile.name;
        variant.selectedFile = null;

        await this.updateSingleVariant(variant);
        this.$dialog.notify.success('Image uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        this.$dialog.notify.error('Failed to upload image');
      }
    },
    openFileInput(variant) {
      this.$refs['fileInput_' + variant.id][0].click();
    },
    handleFileChange(event, variant) {
      const file = event.target.files[0];
      if (file) {
        this.$set(variant, 'selectedFile', file);
        this.uploadFile(variant);
      }
    },
    async createVariant() {
      try {
        // Check if any variant has invalid extra params
        for (const variant of this.variants) {
          if (!this.validateExtraParams(variant)) {
            await this.$dialog.notify.error(`Invalid extra params for variant ${variant.name}: Use format &key=value`, {
              position: 'top-right',
              timeout: 2000,
            });
            return;
          }
        }

        const tempName = `tmp_variant_name_${Math.floor(100 + Math.random() * 900)}`;
        const response = await offerVariants.createOfferVariant(this.offerId, tempName);

        if (response.success) {
          const newVariantId = response.id;
          const newVariant = {
            id: newVariantId,
            name: tempName,
            params: [],
            preSignedUrl: null,
            imageName: null,
            isDefault: false,
            extraParamsInput: '',
            paramError: ''
          };

          this.variants.push(newVariant);

          this.selectedVariantId = newVariantId;
          this.selectedVariant = this.variants.find(v => v.id === newVariantId);

          this.assetData.imageUrl = this.selectedVariant?.preSignedUrl || null;

          // Ensure at least one variant is default
          await this.ensureDefaultVariant();

          await this.$dialog.notify.success('Variant created successfully', {
            position: 'top-right',
            timeout: 1000,
          });
        } else {
          await this.$dialog.notify.error('Variant was not created', {
            position: 'top-right',
            timeout: 2000,
          });
        }
      } catch (error) {
        console.error('Error creating variant:', error);
        await this.$dialog.notify.error('An error occurred while creating variant', {
          position: 'top-right',
          timeout: 2000,
        });
      }
    },
    closeModal() {
      this.dialogDelete = false;
    },
  },
  watch: {
    variants: {
      immediate: true,
      handler(newVariants) {
        newVariants.forEach(variant => {
          if (typeof variant.params === 'string') {
            try {
              variant.params = JSON.parse(variant.params);
            } catch (e) {
              console.error(`Invalid params for variant ${variant.id}`, e);
              variant.params = [];
            }
          }
          if (!Array.isArray(variant.params)) {
            variant.params = [];
          }
        });
        // Ensure at least one variant is default when variants change
        this.ensureDefaultVariant();
      }
    },
    selectedVariantId(newVal) {
      if (!newVal) {
        this.selectedVariant = null;
      } else {
        this.onVariantSelect();
      }
    },
    'selectedVariant.params': function (newValue) {
      if (typeof newValue === 'string') {
        this.selectedVariant.params = JSON.parse(newValue);
      }
    }
  },
  computed: {
    ...mapGetters('offer', ['getOfferVariants']),
  }
}
</script>

<style scoped>
.variant-display {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

/* Header Row */
.variant-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e4e3e3;
  margin-bottom: 10px;
}

.variant-name-header {
  width: 250px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.upload-header {
  width: 300px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.default-header {
  width: 120px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  transform: translateX(32px); /* Shift text to the right without affecting flex layout */
}

.extra-params-header {
  width: 450px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.delete-header {
  width: 24px;
}

.variant-block {
  position: relative;
  width: 100%;
  padding: 8px;
  margin-bottom: 10px;
  background-color: #FFFFFF;
  box-sizing: border-box;
}

.variant-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap; /* Prevent wrapping to maintain consistent layout */
}

.variant-name-input {
  width: 250px;
  height: 40px;
  border: 1px solid #e4e3e3;
  border-radius: 6px;
  padding-left: 10px;
  font-size: 14px;
  box-sizing: border-box;
  flex-shrink: 0; /* Prevent shrinking */
}

.variant-name-input:focus {
  outline: none;
  border-color: #999;
}

.upload-wrapper {
  position: relative;
  width: 300px;
  height: 40px;
  flex-shrink: 0; /* Prevent shrinking */
}

.copy-image-btn {
  position: absolute;
  top: -24px;
  right: -15px;
  font-size: 9px;
  padding: 0;
  min-width: 0;
  background: transparent;
  box-shadow: none;
  height: 18px;
  line-height: 18px;
  letter-spacing: normal;
}

/* Upload Area */
.upload-area-fixed {
  width: 100%;
  height: 40px;
  border: 1px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
}

.variant-image-fixed {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.upload-placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #666;
  line-height: 1.2;
}

.upload-instructions {
  font-weight: 500;
}

.browse-link {
  color: #3498db;
  text-decoration: underline;
  cursor: pointer;
}

.supported-format {
  font-size: 8px;
  color: #999;
  margin-top: 1px;
}

/* Switch */
.default-switch-container {
  display: flex;
  align-items: center;
  justify-content: center; /* Center the switch */
  width: 120px;
  height: 40px; /* Match the height of other elements */
  flex-shrink: 0; /* Prevent shrinking */
}

.default-switch-compact {
  width: 60px;
  margin: 0;
  flex-shrink: 0; /* Prevent the switch from shrinking */
}

/* Ensure the switch track and thumb don't cause layout shifts */
.default-switch-compact .v-input--switch__track {
  background-color: #E0E0E0;
  opacity: 1;
  width: 36px !important; /* Fixed width to prevent layout shift */
  height: 14px !important; /* Fixed height */
}

.default-switch-compact .v-input--switch__thumb {
  background-color: #FFFFFF;
  border: 1px solid #E0E0E0;
  width: 20px !important; /* Fixed width */
  height: 20px !important; /* Fixed height */
}

.default-switch-compact.v-input--is-label-active .v-input--switch__track {
  background-color: #4CAF50;
}

.default-switch-compact.v-input--is-label-active .v-input--switch__thumb {
  background-color: #FFFFFF;
  border: 1px solid #4CAF50;
}

/* Extra Params */
.extra-params-wrapper {
  width: 450px;
  height: 40px;
  flex-shrink: 0; /* Prevent shrinking */
}

.variant-extra-params-input {
  width: 100%;
  height: 100%;
  border: 1px solid #e4e3e3;
  border-radius: 6px;
  padding-left: 10px;
  font-size: 14px;
  box-sizing: border-box;
}

.variant-extra-params-input.invalid-input {
  border-color: #f44336;
}

/* Error Message */
.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 2px;
}

/* Delete Button */
.delete-variant-btn {
  width: 24px;
  height: 24px;
  background-color: #FFFFFF;
  border: 1px solid #E3EEF4;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  flex-shrink: 0; /* Prevent shrinking */
}

.delete-variant-btn:hover {
  background-color: #F5F5F5;
  border-color: #D32F2F;
}

.delete-variant-btn .v-icon {
  color: #FF5C5C;
  font-size: 16px;
}

.delete-variant-btn:hover .v-icon {
  color: #D32F2F;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .variant-header,
  .variant-line {
    flex-wrap: wrap; /* Allow wrapping on smaller screens */
    gap: 8px;
  }

  .variant-name-header,
  .variant-name-input {
    width: 200px;
  }

  .upload-header,
  .upload-wrapper {
    width: 250px;
  }

  .default-header,
  .default-switch-container {
    width: 100px;
  }

  .extra-params-header,
  .extra-params-wrapper {
    width: 300px;
  }
}

@media (max-width: 768px) {
  .variant-name-header,
  .variant-name-input {
    width: 100%; /* Full width on very small screens */
  }

  .upload-header,
  .upload-wrapper {
    width: 100%;
  }

  .default-header,
  .default-switch-container {
    width: 80px;
  }

  .extra-params-header,
  .extra-params-wrapper {
    width: 100%;
  }

  .delete-header,
  .delete-variant-btn {
    width: 24px;
  }
}

/* Remove unused styles */
.v-btn--icon,
.copy-params-btn,
.no-changes-btn,
.no-more-btn {
  display: none;
}
</style>
```